import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db74pibaskivmpnrcft0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessage("");
      return;
    }
    payload.delete("contact_alt");

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
      setMessage("");
    } catch (error) {
      setStatus("error");
      setFormError(error instanceof Error ? error.message : "网络异常，请稍后重试。");
    }
  };

  const inputClass =
    "mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <form
      data-readdy-form
      id="nav-site-contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
        <i className="ri-send-plane-line text-primary-500"></i>
        在线留言
      </h2>
      <p className="mt-1.5 text-sm text-foreground-600">
        有想推荐的网站或建议？留下信息，我们会尽快查看。
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-foreground-800">你的称呼</span>
          <input
            type="text"
            name="name"
            required
            placeholder="请输入称呼"
            className={inputClass}
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-foreground-800">邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="用于接收回复的邮箱"
            className={inputClass}
          />
        </label>
      </div>

      <label className="mt-4 block text-sm">
        <span className="font-medium text-foreground-800">留言内容</span>
        <textarea
          name="message"
          required
          maxLength={500}
          rows={4}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="想推荐的网站、使用建议或其他想法（最多 500 字）"
          className={`${inputClass} resize-none`}
        ></textarea>
        <span className="mt-1 block text-right text-xs text-foreground-400">{message.length}/500</span>
      </label>

      <input
        type="text"
        name="contact_alt"
        className="field-guard-alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && formError ? (
        <p className="mt-4 rounded-md bg-accent-100 px-4 py-2.5 text-sm text-accent-800">
          <i className="ri-error-warning-line mr-1"></i>
          {formError}
        </p>
      ) : null}

      {status === "success" ? (
        <p className="mt-4 rounded-md bg-primary-100 px-4 py-2.5 text-sm text-primary-800">
          <i className="ri-checkbox-circle-line mr-1"></i>
          提交成功，感谢你的留言，我们会尽快处理。
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
        {status === "submitting" ? "提交中..." : "提交留言"}
      </button>
    </form>
  );
}